import type { ReactNode } from 'react';
import { CornerBrackets } from './CornerBrackets';

export type PathKind = 'grog' | 'companion' | 'magus';

const ACCENTS: Record<PathKind, { accent: string; glow: string }> = {
  grog: { accent: '#d9a05a', glow: 'rgba(217, 160, 90, 0.22)' },
  companion: { accent: '#c48862', glow: 'rgba(196, 136, 98, 0.22)' },
  magus: { accent: '#7bd7ff', glow: 'rgba(123, 215, 255, 0.24)' },
};

export function PathCard({
  kind,
  title,
  subtitle,
  desc,
  stats,
  sigil,
  onPick,
}: {
  kind: PathKind;
  title: string;
  subtitle: string;
  desc: string;
  stats: Array<{ value: string; label: string }>;
  sigil: ReactNode;
  onPick: () => void;
}) {
  const a = ACCENTS[kind];
  return (
    <article
      className="path-card group relative flex min-h-[460px] cursor-pointer flex-col overflow-hidden
                 rounded-lg-ornate border p-8 pb-9 text-left
                 bg-[linear-gradient(160deg,rgba(36,26,40,0.85),rgba(14,10,15,0.9))]
                 [transform-style:preserve-3d]
                 transition-[box-shadow,border-color,transform] duration-300
                 hover:-translate-y-1 hover:[transform:translateY(-4px)_rotateX(2deg)]"
      style={
        {
          borderColor: `${a.accent}40`,
          ['--card-accent' as never]: a.accent,
          ['--card-glow' as never]: a.glow,
          // hover shadow — accent-tinted
          boxShadow: 'none',
        } as React.CSSProperties
      }
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow =
          `0 30px 60px rgba(0,0,0,0.6), inset 0 0 0 1px ${a.accent}, 0 0 60px ${a.glow}`;
        e.currentTarget.style.borderColor = a.accent;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = `${a.accent}40`;
      }}
      data-path={kind}
      onClick={onPick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPick();
        }
      }}
      tabIndex={0}
      role="button"
    >
      <CornerBrackets color={a.accent} />
      <span
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 100% 70% at 50% 0%, ${a.glow}, transparent 60%)`,
          opacity: 0.8,
        }}
      />
      <div
        className="relative z-[2] mb-6 h-24 w-24 transition-transform duration-500
                   group-hover:[transform:translateZ(40px)_scale(1.05)]"
        style={{ filter: `drop-shadow(0 0 8px ${a.accent})` }}
      >
        <div
          className="h-full w-full [&_*]:fill-none [&_*]:stroke-[1.25]
                     group-hover:[animation:sigil-spin_14s_linear_infinite]"
          style={{ color: a.accent }}
        >
          <svg
            viewBox="0 0 100 100"
            className="h-full w-full"
            style={{ stroke: a.accent }}
          >
            {sigil}
          </svg>
        </div>
      </div>
      <h3 className="relative z-[2] font-serif text-[34px] font-normal leading-tight tracking-[0.02em] text-parchment-50">
        {title}
      </h3>
      <div
        className="relative z-[2] mt-1 mb-6 font-display text-[11px] uppercase tracking-[0.25em]"
        style={{ color: a.accent }}
      >
        {subtitle}
      </div>
      <p className="relative z-[2] mb-7 text-[14.5px] leading-[1.6] text-parchment-100/70">{desc}</p>
      <div className="relative z-[2] mt-auto flex justify-between border-t border-gold-400/15 pt-5">
        {stats.map((s) => (
          <div key={s.label} className="text-center">
            <div
              className="font-display text-[22px] font-semibold"
              style={{ color: a.accent }}
            >
              {s.value}
            </div>
            <div className="mt-0.5 font-sans text-[10px] uppercase tracking-[0.2em] text-parchment-100/50">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}
